import { createContext, useContext, useId, type ReactNode } from "react";
import { cn } from "../lib/cn";

type FieldCtx = { id: string; invalid: boolean; disabled?: boolean; describedBy?: string };
const FieldContext = createContext<FieldCtx | null>(null);

/** Контролы читают id/invalid/aria-describedby из ближайшего <Field>. */
export const useField = () => useContext(FieldContext);

export type FieldProps = {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Справа от label (например, ссылка «Забыли пароль?»). */
  labelAside?: ReactNode;
  children: ReactNode;
};

export function Field({ label, hint, error, required, disabled, id, className, labelAside, children }: FieldProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const msgId = error || hint ? `${fieldId}-msg` : undefined;
  return (
    <FieldContext.Provider value={{ id: fieldId, invalid: !!error, disabled, describedBy: msgId }}>
      <div className={cn("flex min-w-0 flex-col gap-1", className)}>
        {(label || labelAside) && (
          <div className="flex items-center justify-between gap-2">
            {label && (
              <label htmlFor={fieldId} className="text-xs leading-normal font-medium text-muted">
                {label}
                {required && <span className="ms-0.5 text-danger" aria-hidden>*</span>}
              </label>
            )}
            {labelAside}
          </div>
        )}
        {children}
        {msgId && (
          <p id={msgId} role={error ? "alert" : undefined} className={cn("text-xs", error ? "text-danger" : "text-muted")}>
            {error || hint}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}

/** Сетка формы: 1 колонка на mobile, `cols` с md. */
export function FormGrid({ cols = 2, className, children }: { cols?: 1 | 2 | 3 | 4; className?: string; children: ReactNode }) {
  const map = { 1: "", 2: "md:grid-cols-2", 3: "md:grid-cols-2 xl:grid-cols-3", 4: "md:grid-cols-2 xl:grid-cols-4" };
  return <div className={cn("grid grid-cols-1 gap-4", map[cols], className)}>{children}</div>;
}
